/** One observer and one set of page/device listeners for the entire Explore wall. */
type Connection = EventTarget & { saveData?: boolean; effectiveType?: string };
type Entry = { ratio: number; readyAt: number; granted: boolean; notify: (active: boolean) => void };
const SETTLE_MS = 180;
const MIN_VISIBLE_RATIO = 0.15;

export function createExploreVideoScheduler() {
  const entries = new Map<Element, Entry>();
  let observer: IntersectionObserver | undefined;
  let stop: (() => void) | undefined;
  let timer: number | undefined;
  let limit = 0;
  let suspended = 0;
  let refresh: (() => void) | undefined;

  const revoke = (entry: Entry) => {
    if (!entry.granted) return;
    entry.granted = false;
    entry.notify(false);
  };
  const distribute = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    const now = window.performance.now();
    const eligible = [...entries.values()].filter(entry => entry.ratio >= MIN_VISIBLE_RATIO);
    // Quantized visibility avoids decoder churn for tiny intersection changes.
    const rank = (entry: Entry) => Math.floor(entry.ratio * 4);
    const ready = eligible.filter(entry => entry.readyAt <= now)
      .sort((a, b) => rank(b) - rank(a) || Number(b.granted) - Number(a.granted));
    const selected = new Set(ready.slice(0, limit));
    // Callers synchronously release native media before a new player is admitted.
    for (const entry of entries.values()) if (!selected.has(entry)) revoke(entry);
    for (const entry of selected) if (!entry.granted) { entry.granted = true; entry.notify(true); }
    const pending = eligible.filter(entry => entry.readyAt > now);
    if (limit > 0 && pending.length) {
      timer = window.setTimeout(distribute, Math.max(1, Math.min(...pending.map(entry => entry.readyAt)) - now));
    }
  };

  const start = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 767px)');
    const device = navigator as Navigator & { deviceMemory?: number; connection?: Connection };
    const connection = device.connection;
    const sync = () => {
      const constrained = (device.deviceMemory !== undefined && device.deviceMemory <= 4)
        || (device.hardwareConcurrency > 0 && device.hardwareConcurrency <= 4)
        || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType ?? '');
      const wasSuspended = limit === 0;
      limit = suspended > 0 || document.hidden || reduced.matches || connection?.saveData ? 0
        : constrained || mobile.matches ? 2 : 4;
      // Do not rebuild all native players in the same turn as tab activation.
      // Rapid hide/show sequences should reach a stable visible page first.
      if (wasSuspended && limit > 0) {
        const readyAt = window.performance.now() + SETTLE_MS;
        for (const entry of entries.values()) entry.readyAt = readyAt;
      }
      distribute();
    };
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(changes => {
        for (const change of changes) {
          const entry = entries.get(change.target);
          if (!entry) continue;
          const ratio = change.isIntersecting ? change.intersectionRatio : 0;
          if (entry.ratio < MIN_VISIBLE_RATIO && ratio >= MIN_VISIBLE_RATIO) {
            entry.readyAt = window.performance.now() + SETTLE_MS;
          }
          entry.ratio = ratio;
        }
        distribute();
      }, { threshold: [0, MIN_VISIBLE_RATIO, 0.25, 0.5, 0.75, 1] });
    }
    refresh = sync;
    reduced.addEventListener('change', sync);
    mobile.addEventListener('change', sync);
    connection?.addEventListener?.('change', sync);
    document.addEventListener('visibilitychange', sync);
    sync();
    stop = () => {
      refresh = undefined;
      observer?.disconnect(); observer = undefined;
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      reduced.removeEventListener('change', sync);
      mobile.removeEventListener('change', sync);
      connection?.removeEventListener?.('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  };

  return {
    suspend() {
      suspended += 1;
      refresh?.();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        suspended -= 1;
        refresh?.();
      };
    },
    observe(element: Element, notify: Entry['notify']) {
      if (!entries.size) start();
      const entry: Entry = { ratio: 0, readyAt: 0, granted: false, notify };
      entries.set(element, entry);
      observer?.observe(element);
      return () => {
        if (entries.get(element) !== entry) return;
        observer?.unobserve(element);
        entries.delete(element);
        revoke(entry);
        if (entries.size) distribute();
        else { stop?.(); stop = undefined; }
      };
    },
  };
}

export const exploreVideoScheduler = createExploreVideoScheduler();
